import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import MarkEmailUnreadRounded from "@mui/icons-material/MarkEmailUnreadRounded";
import {
  lastVerificationEmailAt,
  refreshAccountVerification,
  sendAccountVerification,
  signOutFirebase,
} from "services/firebase";
import VerificationFrame, { verificationError } from "./Frame";

function PendingVerification({ user }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [now, setNow] = useState(Date.now());
  const remaining = Math.max(
    0,
    Math.ceil((lastVerificationEmailAt(user.uid) + 60000 - now) / 1000)
  );
  useEffect(() => {
    let active = true;
    if (!lastVerificationEmailAt(user.uid)) {
      setBusy("send");
      sendAccountVerification()
        .then(() => {
          if (active)
            setMessage(
              "E-mail de verificação enviado. Confira sua caixa de entrada e a pasta de spam."
            );
        })
        .catch((failure) => {
          if (active) setError(verificationError(failure));
        })
        .finally(() => {
          if (active) {
            setBusy("");
            setNow(Date.now());
          }
        });
    }
    const check = () => {
      refreshAccountVerification().catch(() => {});
    };
    window.addEventListener("focus", check);
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", check);
    };
  }, [user.uid]);
  const run = async (action) => {
    setBusy(action);
    setError("");
    setMessage("");
    try {
      if (action === "send") {
        await sendAccountVerification();
        setMessage("Enviamos um novo e-mail de verificação. Confira também a pasta de spam.");
      } else if (!(await refreshAccountVerification()))
        setMessage(
          "A verificação ainda está pendente. Abra o link do e-mail para confirmar sua conta."
        );
    } catch (failure) {
      setError(verificationError(failure));
    } finally {
      setBusy("");
      setNow(Date.now());
    }
  };
  return (
    <VerificationFrame>
      <Stack spacing={2.5}>
        <MarkEmailUnreadRounded sx={{ color: "#147d48", fontSize: 42 }} />
        <Chip
          label="Verificação pendente"
          sx={{ alignSelf: "flex-start", bgcolor: "#fff1d5", color: "#815d13", fontWeight: 600 }}
        />
        <Typography
          variant="h4"
          sx={{ color: "#182d27", fontSize: { xs: 25, sm: 29 }, fontWeight: 750 }}
        >
          Confirme seu e-mail
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Para liberar sua conta, abra o e-mail enviado para{" "}
          <strong style={{ overflowWrap: "anywhere" }}>{user.email}</strong> e clique em{" "}
          <strong>Verificar minha conta</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Suas funcionalidades ficam bloqueadas até a confirmação. Depois, vamos conferir o aceite
          dos termos de uso.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {message && <Alert severity="info">{message}</Alert>}
        <Stack spacing={1.5}>
          <Button
            color="success"
            variant="contained"
            disabled={Boolean(busy)}
            onClick={() => run("check")}
            startIcon={
              busy === "check" ? <CircularProgress size={18} color="inherit" /> : undefined
            }
          >
            Já verifiquei meu e-mail
          </Button>
          <Button
            color="success"
            variant="outlined"
            disabled={Boolean(busy) || remaining > 0}
            onClick={() => run("send")}
            startIcon={busy === "send" ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {busy === "send"
              ? "Enviando e-mail…"
              : remaining > 0
              ? `Reenviar e-mail em ${remaining}s`
              : "Reenviar e-mail de verificação"}
          </Button>
          <Button
            onClick={() =>
              signOutFirebase().catch((failure) => setError(verificationError(failure)))
            }
          >
            Sair e usar outra conta
          </Button>
        </Stack>
      </Stack>
    </VerificationFrame>
  );
}
export default function EmailVerificationGate({ user, children }) {
  const { pathname } = useLocation();
  if (!user || user.emailVerified || pathname === "/verificar-conta") return children;
  return <PendingVerification key={user.uid} user={user} />;
}
